import { useState, useEffect, useCallback } from 'react';
import useDebounce from '../../../hooks/useDebounce';

const useSearchLookup = (searchFunction, debounceMs = 300, allowEmptyQuery = false) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const debouncedQuery = useDebounce(query, debounceMs);

  const performSearch = useCallback(async (searchTerm) => {
    if ((!searchTerm.trim() && !allowEmptyQuery) || !searchFunction) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchResults = await searchFunction(searchTerm);
      setResults(searchResults || []);
    } catch (err) {
      console.error('Search error:', err);
      setError(err);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchFunction, allowEmptyQuery]);

  useEffect(() => {
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch]);

  // Clear results when query is empty (only if allowEmptyQuery is false)
  useEffect(() => {
    if (!query.trim() && !allowEmptyQuery) {
      setResults([]);
      setError(null);
    }
  }, [query, allowEmptyQuery]);

  const clearResults = useCallback(() => {
    setResults([]);
    setQuery('');
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    clearResults,
  };
};

export default useSearchLookup;